<template>
  <MeasurePanel
    :view="view"
    v-model:range-distance="rangeDistance"
    v-model:range-bearing="rangeBearing"
    @close="emit('close')"
    @toggle-settings="settingsOpen = !settingsOpen; closeLockMenu()"
    @use-my-location="useMyLocation"
    @pick-on-map="apply(pickOnMap(session))"
    @cancel-pick="apply(cancelPickStart(session))"
    @open-range="openRange"
    @close-range="apply(closeRangeSheet(session))"
    @place-range="placeFromRange"
    @copy-grid="copyGrid"
    @profile-hover="profileHover = $event"
    @toggle-snap="toggleSnapToRoute"
    @create-hike="createHike"
    @toggle-lock-menu="settingsOpen = false; apply(toggleLockMenu(session))"
    @set-lock="chooseLock"
    @set-north="updateSettings({ northReference: $event })"
    @set-bearing-unit="updateSettings({ bearingUnit: $event })"
    @set-grid-format="updateSettings({ gridFormat: $event })"
    @change-tolerance="updateSettings({ toleranceMeters: clampRangeTolerance(settings.toleranceMeters + $event) })"
    @panel-keydown="onKeydown"
  />
  <div v-if="tooltip" class="measure-cursor-tip" :style="tooltip.style">
    <strong>{{ tooltip.main }}</strong><span>{{ tooltip.sub }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, toRaw, watch } from 'vue';
import MeasurePanel from './MeasurePanel.vue';
import { i18n } from '../../i18n';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { HikeTimeUtility, PrettyTime } from '../../utils/helpers/HikeTime';
import { GeoPoint, inverse } from './model/geodesy';
import { effectiveReference, inputToTrue, northCorrectionsAt, parseBearingInput, parseDistance } from './model/bearings';
import { distance as formatDistance, measureUnitsFromPref, measureUnits } from './model/formatters';
import {
  applyLock, cancelPickStart, chooseMyLocation, clampRangeTolerance, closeRangeSheet, dragEnded, dragged, dragStarted, initialSession,
  mapTap, MeasureLock, MeasureMeasurement, MeasureSession, measureStraight, openRangeSheet, pickOnMap, placeRange,
  restartSession, routeActive as isRouteActive, routeChanged, setLock, startDragged, toggleLockMenu, toggleSnap,
} from './model/session';
import {
  closedEvent, durationSeconds, elevationFetchFailedEvent, gridCopiedEvent, locationUnavailableEvent, lockChangedEvent,
  MeasureScreen, openedEvent, pointPlacedEvent, snapToggledEvent, startChosenEvent,
} from './model/telemetry';
import { TrailNetwork } from './trailNetwork';
import { locateOnRoute, measureAlongRoute, snapToRoute } from './routeMeasurement';
import { gridText, measureView, MeasureElevationState, MeasureLocationState, pinLabel } from './measureView';
import { loadMeasureSettings, MeasureSettings, saveMeasureSettings } from './settings';
import { MeasureMapLayers } from './map/MeasureMapLayers';
import { measureFeatures } from './map/mapFeatures';
import { MeasureTargetMarker } from './map/MeasureTargetMarker';
import { MeasureStartDrag } from './map/MeasureStartDrag';
import { ElevationLoader, elevationKey } from './services/elevation';
import { currentLocation } from './services/location';
import { sendMeasureEvent } from './services/analytics';
import { createHikeFromSection } from './services/createHike';

const props = withDefaults(defineProps<{
  context: 'main' | 'hike' | 'recording';
  /** The hike or recording line "Snap to route" measures along; none on the main map. */
  route?: any;
  /** Start straight from this point ("Measure from here"). */
  startAt?: { lng: number; lat: number } | null;
}>(), { route: null, startAt: null });
const emit = defineEmits(['close']);

const NARROW_BELOW = 760;
const SCREENS: Record<string, MeasureScreen> = { main: 'main_map', hike: 'hike_map', recording: 'recording' };
const t = (key: string, params?: Record<string, unknown>) => i18n.global.t(key, params || {}) as string;
const map: any = (window as any).map;

// On a hike page the walker is there to measure the hike, so skip "Where are you measuring from?"
// and go straight to clicking the start on the line.
const session = shallowRef<MeasureSession>(props.context === 'hike' ? pickOnMap(initialSession()) : initialSession());
const settings = ref<MeasureSettings>(loadMeasureSettings());
const units = measureUnitsFor();
const location = ref<MeasureLocationState>({ status: 'idle' });
const elevation = shallowRef<MeasureElevationState>({ status: 'loading' });
const profileHover = ref<number | null>(null);
const preview = shallowRef<GeoPoint | null>(null);
/** While picking the start with snapping on: how far along the hike the previewed point is (m). */
const previewAlong = ref<number | null>(null);
const cursor = ref<{ x: number; y: number } | null>(null);
const modifier = ref<'' | 'Bearing' | 'Distance'>('');
const settingsOpen = ref(false);
const copied = ref(false);
const toast = ref('');
const rangeDistance = ref('1500');
const rangeBearing = ref('');
const narrow = ref(false);
const zoom = ref(map?.getZoom?.() ?? 12);

// The raw GeoJSON, not the parent's reactive copy: the snapper walks every vertex, and through
// the proxy each read would also be tracked as a dependency of the measurement.
const network = computed(() => (props.route ? new TrailNetwork(toRaw(props.route)) : null));
const routeAvailable = computed(() => !!network.value && !network.value.isEmpty);
const snapping = computed(() => isRouteActive(session.value, routeAvailable.value));
const snap = (point: GeoPoint) => snapToRoute(network.value, point);

const corrections = computed(() => {
  const at = session.value.start ?? centre();
  return northCorrectionsAt(at, settings.value.gridFormat, new Date());
});

function measureFor(target: GeoPoint | null): MeasureMeasurement | null {
  if (!target || !session.value.start) return null;
  const s = { ...session.value, target };
  if (snapping.value && network.value) return measureAlongRoute(s, network.value) ?? measureStraight(s);
  return measureStraight(s);
}
const measurement = computed(() => measureFor(session.value.target ?? (session.value.step === 'Target' && !session.value.rangeSheetOpen ? preview.value : null)));

const view = computed(() => measureView({
  t,
  narrow: narrow.value,
  session: session.value,
  measurement: measurement.value,
  settings: settings.value,
  units,
  corrections: corrections.value,
  elevation: elevation.value,
  profileHover: profileHover.value,
  location: location.value,
  routeAvailable: routeAvailable.value,
  copied: copied.value,
  settingsOpen: settingsOpen.value,
  toast: toast.value,
  walkingTime,
}));

// ─── Map drawing ────────────────────────────────────────────────────────────
const layers = map ? new MeasureMapLayers(map) : null;
let marker: MeasureTargetMarker | null = null;

function redraw() {
  if (!layers) return;
  const elevations = elevation.value.status === 'ready' ? elevation.value.elevations : null;
  const m = measurement.value;
  let profilePoint: GeoPoint | null = null;
  if (m && elevations && profileHover.value !== null) {
    profilePoint = m.samplePoints[Math.round(profileHover.value * (m.samplePoints.length - 1))] ?? null;
  }
  const { collections, bandFillMinZoom } = measureFeatures({
    session: session.value,
    measurement: session.value.target ? m : null,
    preview: session.value.target ? null : (m ? m.target : null),
    previewRoute: !session.value.target && m?.routeMode ? m.routePath : null,
    startPreview: session.value.step === 'PickStart' && snapping.value ? preview.value : null,
    profilePoint,
    toleranceMeters: settings.value.toleranceMeters,
    zoom: zoom.value,
  });
  layers.update(collections, bandFillMinZoom);
  syncMarker();
}

function syncMarker() {
  const placed = session.value.target ? measurement.value : null;
  if (!placed) {
    marker?.remove();
    return;
  }
  if (!marker) {
    marker = new MeasureTargetMarker(map, {
      onDragStart: startDrag,
      onDrag: (lngLat) => dragTo({ lat: lngLat.lat, lng: lngLat.lng }),
      onDragEnd: endDrag,
      onKeyDown: onKeydown,
    }, t('measure_tool.measured_point_a11y'));
  }
  marker.show(placed.target);
  marker.setLabel(pinLabel(placed, settings.value, units, corrections.value));
}

watch([session, measurement, elevation, profileHover, settings, zoom, preview], redraw, { deep: false });
watch(() => settings.value, (value) => saveMeasureSettings(value), { deep: true });

// ─── Elevation profile ──────────────────────────────────────────────────────
const loader = new ElevationLoader();
let elevationTimer: ReturnType<typeof setTimeout> | null = null;
let failedKey = '';

watch(measurement, (m) => {
  if (elevationTimer) clearTimeout(elevationTimer);
  if (!m) return;
  if (m.routeElevations) {
    elevation.value = { status: 'ready', elevations: m.routeElevations };
    return;
  }
  const latLngs = m.samplePoints.map((p) => [p.lat, p.lng]);
  const key = elevationKey(latLngs);
  const cached = loader.cached(key);
  if (cached) {
    elevation.value = { status: 'ready', elevations: cached };
    return;
  }
  if (elevation.value.status !== 'ready') elevation.value = { status: 'loading' };
  // A drag or a moving cursor asks again on every frame; wait for it to settle.
  elevationTimer = setTimeout(async () => {
    const result = await loader.load(key, latLngs);
    if (result.status === 'ok') elevation.value = { status: 'ready', elevations: result.elevations };
    if (result.status === 'failed') {
      elevation.value = { status: 'unavailable' };
      if (failedKey !== key) sendMeasureEvent(elevationFetchFailedEvent(latLngs.length, null));
      failedKey = key;
    }
  }, session.value.dragging || startPinDrag?.dragging ? 250 : 150);
});

// ─── Actions ────────────────────────────────────────────────────────────────
function apply(next: MeasureSession) {
  session.value = next;
}

async function useMyLocation() {
  location.value = { status: 'finding' };
  const fix = await currentLocation();
  if (!fix.ok) {
    location.value = { status: fix.reason === 'denied' ? 'denied' : 'unavailable' };
    sendMeasureEvent(locationUnavailableEvent(fix.reason === 'denied' ? 'permission' : 'no_fix'));
    return;
  }
  location.value = { status: 'idle', accuracy: fix.accuracy };
  apply(chooseMyLocation(session.value, { lat: fix.lat, lng: fix.lng }));
  sendMeasureEvent(startChosenEvent('MyLocation'));
  map?.easeTo?.({ center: [fix.lng, fix.lat] });
}

function onMapClick(event: any) {
  // Markers sit inside the map's canvas container, so a click on the pin reaches the map too.
  if (marker && event.originalEvent?.target instanceof Node && marker.element.contains(event.originalEvent.target)) return;
  // Nor does pressing (or nudging) the start pin move the measured point onto it.
  if (startPinDrag && (startPinDrag.dragging || startPinDrag.isOnStart(event))) return;
  const point = { lat: event.lngLat.lat, lng: event.lngLat.lng };
  const before = session.value;
  // Once placed, a click elsewhere moves the point there, keeping the lock.
  if (before.target && before.start && !before.rangeSheetOpen && !before.dragging) {
    apply({ ...before, target: applyLock(point, before.start, before, snapping.value, snap), lockMenuOpen: false });
    profileHover.value = null;
    return;
  }
  const next = mapTap(before, point, snapping.value, snap);
  if (next === before) return;
  apply(next);
  preview.value = null;
  if (before.step === 'PickStart') sendMeasureEvent(startChosenEvent('MapPoint'));
  else if (!before.target && next.target) sendMeasureEvent(pointPlacedEvent('tap'));
}

function onMapMove(event: any) {
  if (startPinDrag?.dragging) return;
  const s = session.value;
  const placing = s.step === 'Target' && !s.target && !s.rangeSheetOpen;
  const picking = s.step === 'PickStart';
  const raw = { lat: event.lngLat.lat, lng: event.lngLat.lng };
  if (placing && !narrow.value) {
    preview.value = snapping.value ? snap(raw) : raw;
  } else if (picking) {
    // Where the start will land: on the line, with how far along the hike that is, when snapping.
    const found = snapping.value ? locateOnRoute(network.value, raw) : null;
    preview.value = found ? found.point : raw;
    previewAlong.value = found ? found.along : null;
  }
  const pointer = { x: event.originalEvent.clientX, y: event.originalEvent.clientY };
  // Snapping, the tip rides the line with the previewed point rather than trailing the pointer.
  const anchor = snapping.value && preview.value && (placing || picking) ? screenPoint(preview.value) ?? pointer : pointer;
  cursor.value = narrow.value || !(placing || picking) ? null : anchor;
}

/** Where a map point is on screen, in the page coordinates the fixed cursor tip uses. */
function screenPoint(point: GeoPoint): { x: number; y: number } | null {
  const at = map?.project?.([point.lng, point.lat]);
  const box = map?.getContainer?.()?.getBoundingClientRect?.();
  return at && box ? { x: box.left + at.x, y: box.top + at.y } : null;
}

function onMapLeave() {
  cursor.value = null;
  if (!session.value.target) preview.value = null;
}

function openRange() {
  closeLockMenu();
  settingsOpen.value = false;
  let next = session.value;
  if (next.target) next = { ...next, target: null, lock: 'Free', band: false };
  apply(openRangeSheet(next));
}

function placeFromRange() {
  const meters = parseDistance(rangeDistance.value);
  const typed = parseBearingInput(rangeBearing.value, settings.value.bearingUnit);
  if (meters === null || typed === null || !session.value.start) {
    flash(t('measure_tool.range_invalid'));
    return;
  }
  const reference = effectiveReference(corrections.value, settings.value.northReference);
  const trueBearing = inputToTrue(typed, settings.value.bearingUnit, reference, corrections.value);
  apply(placeRange(session.value, meters, trueBearing));
  sendMeasureEvent(pointPlacedEvent('range_bearing'));
  const target = session.value.target;
  if (target && map?.fitBounds) {
    const s = session.value.start;
    map.fitBounds([[Math.min(s.lng, target.lng), Math.min(s.lat, target.lat)], [Math.max(s.lng, target.lng), Math.max(s.lat, target.lat)]], { padding: 120, maxZoom: 16 });
  }
}

function chooseLock(mode: MeasureLock) {
  apply(setLock(session.value, mode));
  sendMeasureEvent(lockChangedEvent(mode));
}

function closeLockMenu() {
  if (session.value.lockMenuOpen) apply({ ...session.value, lockMenuOpen: false });
}

function toggleSnapToRoute() {
  apply(toggleSnap(session.value, snap));
  sendMeasureEvent(snapToggledEvent(session.value.routeOn));
}

function updateSettings(change: Partial<MeasureSettings>) {
  settings.value = { ...settings.value, ...change };
}

async function copyGrid() {
  const m = measurement.value;
  if (!m) return;
  try {
    await navigator.clipboard?.writeText(gridText(t, settings.value.gridFormat, m.target.lat, m.target.lng));
  } catch (error) {
    // The figure is on screen either way.
  }
  copied.value = true;
  setTimeout(() => { copied.value = false; }, 1600);
  flash(t('measure_tool.grid_copied'));
  sendMeasureEvent(gridCopiedEvent(settings.value.gridFormat));
}

function createHike() {
  const path = measurement.value?.routePath;
  if (!path || !createHikeFromSection(path.map((p) => [p.lng, p.lat]))) window.alert(t('measure_tool.create_hike_failed'));
}

let toastTimer: ReturnType<typeof setTimeout> | null = null;
function flash(message: string) {
  toast.value = message;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = ''; }, 1800);
}

// ─── Dragging the pin ───────────────────────────────────────────────────────
let dragCount = 0;
let dragLine: { distance: number; initialBearing: number } | null = null;

function startDrag() {
  if (!session.value.start || !session.value.target) return;
  dragLine = inverse(session.value.start, session.value.target);
  profileHover.value = null;
  dragCount += 1;
  apply(dragStarted(session.value));
}

function dragTo(raw: GeoPoint) {
  let s = session.value;
  // Shift / Alt hold the bearing / distance for this drag only, as the free lock's shortcuts.
  if (s.lock === 'Free' && modifier.value && dragLine) {
    s = { ...s, lock: modifier.value, lockedRange: dragLine.distance, lockedBearing: dragLine.initialBearing };
  }
  const next = dragged(s, raw, snapping.value, snap);
  apply({ ...next, lock: session.value.lock, lockedRange: session.value.lockedRange, lockedBearing: session.value.lockedBearing });
}

function endDrag() {
  modifier.value = '';
  apply(dragEnded(session.value));
}

function trackModifiers(event: PointerEvent | KeyboardEvent) {
  modifier.value = event.shiftKey ? 'Bearing' : event.altKey ? 'Distance' : '';
}

// ─── Dragging the start ─────────────────────────────────────────────────────
const startPinDrag = map ? new MeasureStartDrag(map, {
  onDragStart: () => {
    profileHover.value = null;
    cursor.value = null;
    apply({ ...session.value, lockMenuOpen: false });
  },
  onDrag: (lngLat) => apply(startDragged(session.value, { lat: lngLat.lat, lng: lngLat.lng }, snapping.value, snap)),
  onDragEnd: () => apply({ ...session.value }),
  restCursor: () => (crosshairStep() ? 'crosshair' : ''),
}) : null;

function crosshairStep(): boolean {
  const s = session.value;
  return s.step === 'PickStart' || (s.step === 'Target' && !s.target && !s.rangeSheetOpen);
}

// ─── Keyboard: Esc steps back; arrows nudge the focused pin ─────────────────
function onKeydown(event: KeyboardEvent) {
  const s = session.value;
  if (event.key === 'Escape') {
    event.preventDefault();
    if (s.lockMenuOpen) return closeLockMenu();
    if (settingsOpen.value) { settingsOpen.value = false; return; }
    if (s.rangeSheetOpen) return apply(closeRangeSheet(s));
    if (s.target) return apply({ ...s, target: null, lock: 'Free', band: false, dragging: false });
    if (s.step !== 'Start') return apply(restartSession(s));
    return;
  }
  const nudge: Record<string, [number, number]> = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
  const step = nudge[event.key];
  if (!step || !s.target || !map || event.target !== marker?.element) return;
  event.preventDefault();
  const px = event.shiftKey ? 10 : 1;
  const at = map.project([s.target.lng, s.target.lat]);
  const moved = map.unproject([at.x + step[0] * px, at.y + step[1] * px]);
  const constrained = dragged({ ...s, dragging: true }, { lat: moved.lat, lng: moved.lng }, snapping.value, snap);
  apply({ ...constrained, dragging: false });
}

// ─── Lifecycle ──────────────────────────────────────────────────────────────
const openedAt = Date.now();
const onZoom = () => { zoom.value = map.getZoom(); };
const onResize = () => { narrow.value = (map?.getContainer?.()?.clientWidth ?? window.innerWidth) < NARROW_BELOW; };
const onCanvasKeydown = (event: KeyboardEvent) => { if (event.key === 'Escape') onKeydown(event); };

watch(network, () => apply(routeChanged(session.value, routeAvailable.value, snap)));
// "Measure from here" while the tool is already open starts again from that point.
watch(() => props.startAt, (point) => { if (point) startFrom(point); });

function startFrom(point: { lng: number; lat: number }) {
  apply(mapTap(pickOnMap(restartSession(session.value)), { lat: point.lat, lng: point.lng }, snapping.value, snap));
  preview.value = null;
  sendMeasureEvent(startChosenEvent('MapPoint'));
}
watch(() => [session.value.step, session.value.target, session.value.rangeSheetOpen], () => {
  if (startPinDrag?.dragging) return;
  const crosshair = crosshairStep();
  const canvas = map?.getCanvas?.();
  if (canvas) canvas.style.cursor = crosshair ? 'crosshair' : '';
  if (!crosshair) cursor.value = null;
});

const tooltip = computed(() => {
  const c = cursor.value;
  const s = session.value;
  if (!c || narrow.value) return null;
  let main = '';
  let sub = '';
  if (s.dragging && modifier.value) {
    const bearing = modifier.value === 'Bearing';
    main = bearing ? t('measure_tool.tooltip_bearing_kept') : t('measure_tool.tooltip_distance_kept');
    sub = bearing ? t('measure_tool.tooltip_holding_shift') : t('measure_tool.tooltip_holding_alt');
  } else if (s.step === 'PickStart' && preview.value) {
    // On the line, how far along the hike; elsewhere, the grid reference.
    main = snapping.value && previewAlong.value !== null
      ? formatDistance(previewAlong.value, units)
      : gridText(t, settings.value.gridFormat, preview.value.lat, preview.value.lng);
    sub = t('measure_tool.tooltip_set_start');
  } else if (measurement.value && !s.target) {
    main = pinLabel(measurement.value, settings.value, units, corrections.value);
    sub = t('measure_tool.tooltip_drop');
  }
  if (!main) return null;
  const left = c.x + 18 + 220 > window.innerWidth ? c.x - 18 - 200 : c.x + 18;
  return { main, sub, style: { left: `${left}px`, top: `${c.y + 18}px` } };
});

onMounted(() => {
  (window as any).mainMapMeasurementMode = true;
  onResize();
  if (map) {
    layers?.attach();
    startPinDrag?.attach();
    map.on('click', onMapClick);
    map.on('mousemove', onMapMove);
    map.on('mouseout', onMapLeave);
    map.on('zoom', onZoom);
    map.on('resize', onResize);
    map.getCanvasContainer?.()?.addEventListener('keydown', onCanvasKeydown);
  }
  window.addEventListener('pointermove', trackModifiers, true);
  window.addEventListener('keydown', trackModifiers, true);
  window.addEventListener('keyup', trackModifiers, true);
  window.addEventListener('resize', onResize);
  if (props.startAt) startFrom(props.startAt);
  sendMeasureEvent(openedEvent(SCREENS[props.context], routeAvailable.value));
  redraw();
});

onBeforeUnmount(() => {
  (window as any).mainMapMeasurementMode = false;
  if (map) {
    map.off('click', onMapClick);
    map.off('mousemove', onMapMove);
    map.off('mouseout', onMapLeave);
    map.off('zoom', onZoom);
    map.off('resize', onResize);
    map.getCanvasContainer?.()?.removeEventListener('keydown', onCanvasKeydown);
    const canvas = map.getCanvas?.();
    if (canvas) canvas.style.cursor = '';
  }
  window.removeEventListener('pointermove', trackModifiers, true);
  window.removeEventListener('keydown', trackModifiers, true);
  window.removeEventListener('keyup', trackModifiers, true);
  window.removeEventListener('resize', onResize);
  if (elevationTimer) clearTimeout(elevationTimer);
  if (toastTimer) clearTimeout(toastTimer);
  marker?.remove();
  startPinDrag?.detach();
  layers?.detach();
  const s = session.value;
  sendMeasureEvent(closedEvent({
    durationSeconds: durationSeconds(openedAt, Date.now()),
    placed: !!s.target,
    mode: snapping.value ? 'route' : 'straight',
    lock: s.lock,
    dragCount,
    northReference: settings.value.northReference,
    bearingUnit: settings.value.bearingUnit,
    gridFormat: settings.value.gridFormat,
  }));
});

function centre(): GeoPoint {
  const c = map?.getCenter?.();
  return c ? { lat: c.lat, lng: c.lng } : { lat: 0, lng: 0 };
}

function measureUnitsFor() {
  try {
    return measureUnitsFromPref(MeasurementPrefUtility.preference());
  } catch (error) {
    return measureUnits(true);
  }
}

function walkingTime(distanceMeters: number, ascentMeters: number): string {
  const seconds = HikeTimeUtility.preference().secondsDuration(distanceMeters, ascentMeters);
  // Whole minutes, never under one, as the apps show it.
  return new PrettyTime(Math.max(60, Math.round(seconds / 60) * 60)).prettyTime();
}
</script>

<style>
/* The end marker and its label pill live in a Mapbox marker, outside this component's scope. */
.measure-target-pill {
  position: absolute;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 124px;
  height: 26px;
  box-sizing: border-box;
  padding: 0 12px;
  border-radius: 13px;
  background: #17181d;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  transform: translateX(-50%);
  pointer-events: none;
}
.measure-target:focus-visible {
  outline: none;
}
.measure-target:focus-visible .measure-target-dot {
  box-shadow: 0 0 0 3px #fddf00 !important;
}
.measure-cursor-tip {
  position: fixed;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #17181d;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 12px;
  pointer-events: none;
}
.measure-cursor-tip span {
  color: #acb0bf;
  font-size: 11px;
  font-weight: 500;
}
</style>
